// src/components/bsm/CourseMatcherQuiz.js
import React, { useState } from 'react';
import { CheckIcon, RotateCcwIcon, ChevronRightIcon, BoltIcon, GraduationCapIcon } from './BsmIcons';

const QUIZ_QUESTIONS = [
  {
    id: 'exp',
    title: 'What is your current stock market experience?',
    subtitle: 'Step 1 of 3 • Evaluating baseline knowledge',
    options: [
      {
        id: 'beginner',
        label: 'Complete Beginner',
        desc: 'Zero prior knowledge or just started watching Youtube/news',
        targetCourse: "Beginner's Stock Market Course",
        points: { level1: 3, level2: 1, level3: 0 },
      },
      {
        id: 'intermediate',
        label: 'Occasional Investor / Swing Aspirant',
        desc: 'Own a Demat account, have bought a few shares, want systematic setups',
        targetCourse: 'Swing Master (Power of Equity)',
        points: { level1: 1, level2: 3, level3: 1 },
      },
      {
        id: 'experienced',
        label: 'Active Intraday / Derivative Trader',
        desc: 'Trading F&O or equity but suffering from erratic drawdowns and lack of edge',
        targetCourse: 'Master Course (Full-Time Trader)',
        points: { level1: 0, level2: 1, level3: 3 },
      },
    ],
  },
  {
    id: 'time',
    title: 'How much time can you commit on market days?',
    subtitle: 'Step 2 of 3 • Aligning execution bandwidth',
    options: [
      {
        id: 'weekend',
        label: '30-45 minutes / Evening & Weekends',
        desc: 'Salaried professional needing end-of-day scanners without screen-gluing',
        points: { level1: 2, level2: 3, level3: 0 },
      },
      {
        id: 'flexible',
        label: '1 to 2 hours per day',
        desc: 'Can review market morning open (9:15-10:30 AM) or closing hour (2:30-3:30 PM)',
        points: { level1: 1, level2: 3, level3: 2 },
      },
      {
        id: 'fulltime',
        label: 'Full Market Hours (9:15 AM - 3:30 PM)',
        desc: 'Dedicated to mastering live floor scalping, terminal operations & intraday setups',
        points: { level1: 0, level2: 1, level3: 3 },
      },
    ],
  },
  {
    id: 'goal',
    title: 'What is your primary trading goal for the next 12 months?',
    subtitle: 'Step 3 of 3 • Defining outcome roadmap',
    options: [
      {
        id: 'safe_foundation',
        label: 'Build Strong Fundamentals & Avoid Losses',
        desc: 'Master price action basics, order types, chart reading & risk boundaries',
        points: { level1: 3, level2: 1, level3: 0 },
      },
      {
        id: 'secondary_income',
        label: 'Consistent Secondary Income via Equity Swings',
        desc: 'Compound wealth using multi-day breakout setups with strictly 1:3 risk-reward',
        points: { level1: 1, level2: 3, level3: 1 },
      },
      {
        id: 'derivatives_mastery',
        label: 'Professional Scalping & Option Derivatives',
        desc: 'Decode Option Chains, Delta/Theta Greeks, and multi-monitor execution',
        points: { level1: 0, level2: 1, level3: 3 },
      },
    ],
  },
];

const COURSE_MAP = {
  level1: {
    title: "Beginner's Stock Market Course",
    badge: 'Level 1 • Foundation Pathway',
    duration: '3 Weeks (15 Live Sessions)',
    mode: 'Pune Classroom (Akurdi) & Online',
    matchPct: '98%',
    summary: 'Best suited for salaried employees and beginners wanting crystal-clear clarity on Demat operations, chart anatomy, and capital protection without confusing jargon.',
  },
  level2: {
    title: 'Swing Master (Power of Equity)',
    badge: 'Level 2 • High-Probability Momentum',
    duration: '4 Weeks (20 Live Sessions)',
    mode: 'Pune Classroom & Online',
    matchPct: '99%',
    summary: 'The ideal sweet spot for busy professionals. Learn how to identify multibagger breakout patterns in just 30 minutes a day with rigorous 1:3 risk-reward discipline.',
  },
  level3: {
    title: 'Master Course (Full-Time Trader)',
    badge: 'Level 3 • Advanced Derivatives & Greeks',
    duration: '6 Weeks (30 Live Sessions)',
    mode: 'Pune Classroom & Online',
    matchPct: '97%',
    summary: 'Designed for serious full-time aspirants. Covers live market floor execution, deep Option Chain open interest decoding, Greeks hedging, and psychological discipline.',
  },
};

export default function CourseMatcherQuiz({ onSelectCourse }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const handleSelectOption = (questionId, option) => {
    const updatedAnswers = { ...answers, [questionId]: option };
    setAnswers(updatedAnswers);

    if (currentStep < QUIZ_QUESTIONS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Calculate scores
      let scores = { level1: 0, level2: 0, level3: 0 };
      Object.values(updatedAnswers).forEach((opt) => {
        scores.level1 += opt.points.level1;
        scores.level2 += opt.points.level2;
        scores.level3 += opt.points.level3;
      });

      let winner = 'level2';
      if (scores.level1 >= scores.level2 && scores.level1 >= scores.level3) {
        winner = 'level1';
      } else if (scores.level3 >= scores.level2 && scores.level3 >= scores.level1) {
        winner = 'level3';
      }
      setResult(COURSE_MAP[winner]);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAnswers({});
    setResult(null);
  };

  return (
    <div className="quiz-matcher-container">
      <div className="quiz-matcher-header">
        <div className="quiz-header-badge">
          <BoltIcon width={13} height={13} />
          <span>Interactive Pathway Matcher</span>
        </div>
        <h3>Confused about where to begin? Find your optimal learning track.</h3>
        <p>Answer 3 quick questions to receive a personalized curriculum recommendation aligned with your schedule and capital.</p>
      </div>

      {!result ? (
        <div className="quiz-card-step">
          <div className="quiz-progress-bar">
            {QUIZ_QUESTIONS.map((_, idx) => (
              <div
                key={idx}
                className={`quiz-progress-segment ${idx <= currentStep ? 'active' : ''}`}
              />
            ))}
          </div>

          <div className="quiz-step-title-box">
            <span className="quiz-step-sub">{QUIZ_QUESTIONS[currentStep].subtitle}</span>
            <h4 className="quiz-step-question">{QUIZ_QUESTIONS[currentStep].title}</h4>
          </div>

          <div className="quiz-options-grid">
            {QUIZ_QUESTIONS[currentStep].options.map((option) => (
              <button
                type="button"
                key={option.id}
                className="quiz-option-card"
                onClick={() => handleSelectOption(QUIZ_QUESTIONS[currentStep].id, option)}
              >
                <div className="quiz-option-main">
                  <strong>{option.label}</strong>
                  <p>{option.desc}</p>
                </div>
                <div className="quiz-option-arrow">
                  <ChevronRightIcon width={16} height={16} />
                </div>
              </button>
            ))}
          </div>

          {currentStep > 0 && (
            <div className="quiz-back-row">
              <button
                type="button"
                className="quiz-btn-prev"
                onClick={() => setCurrentStep((prev) => prev - 1)}
              >
                Back to previous question
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="quiz-result-card">
          <div className="quiz-result-badge-row">
            <span className="quiz-match-pill">
              <CheckIcon width={13} height={13} />
              <span>{result.matchPct} Match Recommendation</span>
            </span>
            <span className="quiz-track-badge">{result.badge}</span>
          </div>

          <h4 className="quiz-result-title">{result.title}</h4>
          <p className="quiz-result-summary">{result.summary}</p>

          <div className="quiz-result-meta-row">
            <div className="meta-box">
              <span className="meta-label">Duration</span>
              <strong>{result.duration}</strong>
            </div>
            <div className="meta-box">
              <span className="meta-label">Campus &amp; Mode</span>
              <strong>{result.mode}</strong>
            </div>
          </div>

          <div className="quiz-result-actions">
            <button
              type="button"
              className="btn-quiz-enroll"
              onClick={() => onSelectCourse(result.title)}
            >
              <GraduationCapIcon width={16} height={16} />
              <span>Apply for {result.title}</span>
            </button>
            <button
              type="button"
              className="btn-quiz-retake"
              onClick={handleReset}
            >
              <RotateCcwIcon width={14} height={14} />
              <span>Retake Matcher</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
